import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LanguageSection = ({ formData, onChange }) => {
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Language & Communication
        </h2>
        <p className="text-accent-600">
          Set your language preferences and manage notification settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Language */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Preferred Language *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Languages" size={18} className="text-accent-400" />
            </div>
            <select
              value={formData?.languagePreference || ''}
              onChange={(e) => onChange('languagePreference', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select a language</option>
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notification Preferences */}
        <div>
          <h3 className="font-heading font-semibold text-accent-800 mb-4">
            Notification Preferences
          </h3>

          <div className="space-y-4">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Mail" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Email Notifications</h4>
                  <p className="text-sm text-accent-600">
                    Receive important updates and reminders via email
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.email?.newsletter || false}
                onChange={(e) => onChange('notificationPreferences', {
                  ...formData?.notificationPreferences,
                  email: {
                    ...formData?.notificationPreferences?.email,
                    newsletter: e.target.checked
                  }
                })}
              />
            </div>

            {/* Promotional Emails */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Star" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Promotional Emails</h4>
                  <p className="text-sm text-accent-600">
                    Receive newsletters, promotions, and gift ideas
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.email?.promotions || false}
                onChange={(e) => onChange('notificationPreferences', {
                  ...formData?.notificationPreferences,
                  email: {
                    ...formData?.notificationPreferences?.email,
                    promotions: e.target.checked
                  }
                })}
              />
            </div>

            {/* Account Updates */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Shield" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Account Updates</h4>
                  <p className="text-sm text-accent-600">
                    Security alerts and important account information
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.email?.account || false}
                onChange={(e) => onChange('notificationPreferences', {
                  ...formData?.notificationPreferences,
                  email: {
                    ...formData?.notificationPreferences?.email,
                    account: e.target.checked
                  }
                })}
              />
            </div>

            {/* Giveaway Notifications */}
            <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Gift" size={20} className="text-accent-600" />
                <div>
                  <h4 className="font-medium text-accent-800">Giveaway Notifications</h4>
                  <p className="text-sm text-accent-600">
                    Get notified about new giveaways and contests
                  </p>
                </div>
              </div>
              <Input
                type="checkbox"
                checked={formData?.notificationPreferences?.email?.giveaways || false}
                onChange={(e) => onChange('notificationPreferences', {
                  ...formData?.notificationPreferences,
                  email: {
                    ...formData?.notificationPreferences?.email,
                    giveaways: e.target.checked
                  }
                })}
              />
            </div>
          </div>
        </div>

        {/* Communication Preferences */}
        <div className="bg-primary-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-primary-800 mb-1">
                Communication Tips
              </h4>
              <p className="text-sm text-primary-700">
                We recommend keeping email notifications enabled to stay updated on your
                orders and important account information. You can always customize these
                settings later.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSection;